import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import type { Order } from '../types';
import { StatusBadge } from './StatusBadge';
import { useUpdateOrder, useCheckStatus, useAiAnalysis, useNotifyCs, useNotifyDevTeam, useExportJob, useResubmitOrder } from '../hooks/useOrders';
import { AiModelSelect } from './AiModelSelect';
import { DEFAULT_AI_MODEL, type AiModelKey } from '../utils/aiModels';

interface OrderRowProps {
  order: Order;
}

export function OrderRow({ order }: OrderRowProps) {
  const [notes, setNotes] = useState(order.notes || '');
  const [isExpanded, setIsExpanded] = useState(false);
  const [showPayload, setShowPayload] = useState(false);
  const [aiModel, setAiModel] = useState<AiModelKey>(DEFAULT_AI_MODEL);
  const updateOrder = useUpdateOrder();
  const checkStatus = useCheckStatus();
  const aiAnalysis = useAiAnalysis();
  const notifyCsMutation = useNotifyCs();
  const notifyDevMutation = useNotifyDevTeam();
  const exportJobMutation = useExportJob();
  const resubmitOrderMutation = useResubmitOrder();

  const handleToggleStatus = () => {
    const newStatus = order.status === 'pending' ? 'resolved' : 'pending';
    updateOrder.mutate({
      date: order.date,
      orderId: order.orderId,
      status: newStatus,
      notes: notes || undefined,
    });
  };

  const handleSaveNotes = () => {
    updateOrder.mutate({
      date: order.date,
      orderId: order.orderId,
      status: order.status,
      notes,
    });
  };

  // Get display columns from csvData
  const csvColumns = Object.entries(order.csvData).filter(
    ([key]) => key !== 'orderId'
  );
  const canResubmitMissingOrder =
    order.status === 'pending' &&
    order.airbossOrderExists === false &&
    !order.airbossJobId &&
    order.resubmitStatus !== 'submitted';
  const resubmitError =
    resubmitOrderMutation.data?.error ||
    (resubmitOrderMutation.error instanceof Error ? resubmitOrderMutation.error.message : undefined) ||
    (order.resubmitStatus === 'error' ? order.resubmitError : undefined);

  return (
    <>
      <tr
        onClick={() => setIsExpanded(!isExpanded)}
        className={`hover:bg-gray-50 cursor-pointer ${order.status === 'resolved' ? 'bg-gray-50' : ''}`}
      >
        <td className="px-4 py-3 whitespace-nowrap">
          <span className="text-gray-400">
            <svg
              className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </span>
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <span className="font-mono text-sm text-gray-900">{order.orderId}</span>
        </td>
        <td className="px-4 py-3">
          <span className="text-sm text-gray-600">
            {order.reason || '-'}
          </span>
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <div className="flex items-center gap-1.5">
            <StatusBadge status={
              order.status === 'pending' && order.reason !== 'payment pending'
                ? 'action_required'
                : order.status
            } />
            {order.status === 'pending' && (order.airbossJobFailed || order.airbossFlagged) && (
              <span title={
                order.aiStatus === 'complete' ? 'AI analysis complete' :
                (order.aiStatus === 'started' || order.aiStatus === 'fetching-context' || order.aiStatus === 'analyzing') ? 'AI analysis in progress' :
                'AI analysis available'
              }>
                <svg className={`w-4 h-4 ${
                  order.aiStatus === 'complete' ? 'text-green-500' :
                  (order.aiStatus === 'started' || order.aiStatus === 'fetching-context' || order.aiStatus === 'analyzing') ? 'text-purple-400 animate-pulse' :
                  'text-purple-400'
                }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </span>
            )}
          </div>
        </td>
        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
          {order.resolvedBy || '-'}
        </td>
        <td className="px-4 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
          <div className="flex flex-wrap items-center gap-2">
            {order.status === 'pending' ? (
              <button
                onClick={() => checkStatus.mutate({ date: order.date, orderId: order.orderId })}
                disabled={checkStatus.isPending}
                className="px-3 py-1 text-sm font-medium rounded-md transition-colors bg-blue-100 text-blue-700 hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {checkStatus.isPending ? 'Checking...' : 'Check Status'}
              </button>
            ) : (
              <button
                onClick={handleToggleStatus}
                disabled={updateOrder.isPending}
                className="px-3 py-1 text-sm font-medium rounded-md transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updateOrder.isPending ? 'Saving...' : 'Unresolve'}
              </button>
            )}
            {order.status === 'pending' && !!order.notes && (
              <button
                onClick={handleToggleStatus}
                disabled={updateOrder.isPending}
                className="px-3 py-1 text-sm font-medium rounded-md transition-colors bg-green-100 text-green-700 hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updateOrder.isPending ? 'Saving...' : 'Resolve Order'}
              </button>
            )}
            {canResubmitMissingOrder && (
              <button
                onClick={() => {
                  if (window.confirm('Resubmit this order to Airboss?')) {
                    resubmitOrderMutation.mutate({ date: order.date, orderId: order.orderId });
                  }
                }}
                disabled={resubmitOrderMutation.isPending}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  order.resubmitStatus === 'error'
                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                    : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {resubmitOrderMutation.isPending
                  ? 'Resubmitting...'
                  : order.resubmitStatus === 'error'
                    ? 'Retry Resubmit'
                    : 'Resubmit'}
              </button>
            )}
            {order.slackCancelEligible && (order.aiStatus === 'complete' || !order.aiStatus) && (
              <button
                onClick={() => notifyCsMutation.mutate({ date: order.date, orderId: order.orderId })}
                disabled={notifyCsMutation.isPending || notifyCsMutation.isSuccess}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  notifyCsMutation.isSuccess
                    ? 'bg-gray-100 text-gray-500'
                    : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {notifyCsMutation.isPending
                  ? 'Sending...'
                  : notifyCsMutation.isSuccess
                    ? 'Sent to CS'
                    : 'Notify CS'}
              </button>
            )}
            {order.devNotifyEligible && (
              <button
                onClick={() => notifyDevMutation.mutate({ date: order.date, orderId: order.orderId })}
                disabled={notifyDevMutation.isPending || notifyDevMutation.isSuccess}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  notifyDevMutation.isSuccess
                    ? 'bg-gray-100 text-gray-500'
                    : 'bg-rose-100 text-rose-700 hover:bg-rose-200'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {notifyDevMutation.isPending
                  ? 'Sending...'
                  : notifyDevMutation.isSuccess
                    ? 'Sent to Dev Team'
                    : 'Notify Dev Team'}
              </button>
            )}
          </div>
        </td>
      </tr>

      {isExpanded && (
        <tr className="bg-gray-50">
          <td colSpan={6} className="px-4 py-4">
            <div className="space-y-4">
              {/* CSV Data */}
              {csvColumns.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Order Details
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {csvColumns.map(([key, value]) => (
                      <div key={key} className="text-sm">
                        <span className="text-gray-500">{key}:</span>{' '}
                        {key === 'shopify_order_id' ? (
                          <a
                            href={`https://admin.shopify.com/store/skimsbody/orders/${value}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {value}
                          </a>
                        ) : (
                          <span className="text-gray-900">{value}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Airboss Links */}
              {order.airbossOrderExists !== undefined && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Airboss
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div className="text-sm">
                      <span className="text-gray-500">Order:</span>{' '}
                      {order.airbossOrderExists ? (
                        <a
                          href={`https://airboss-prod.com/airboss-admin/orders/order/?q=${order.orderId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Found
                        </a>
                      ) : (
                        <span className="text-gray-400">Not found</span>
                      )}
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-500">Job:</span>{' '}
                      {order.airbossJobId ? (
                        <a
                          href={`https://airboss-prod.com/airboss-admin/orders/job/${order.airbossJobId}/change/`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {order.airbossJobId}
                        </a>
                      ) : (
                        <span className="text-gray-400">Not found</span>
                      )}
                    </div>
                    {order.resubmitStatus && (
                      <div className="text-sm">
                        <span className="text-gray-500">Resubmit:</span>{' '}
                        {order.resubmitStatus === 'submitted' ? (
                          <span className="text-emerald-700 font-medium">
                            Submitted
                            {order.resubmittedAt && (
                              <span className="text-gray-500 font-normal">
                                {' '}
                                {new Date(order.resubmittedAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="text-red-700 font-medium">Failed</span>
                        )}
                      </div>
                    )}
                    {order.airbossFlagged && (
                      <div className="text-sm col-span-2">
                        <span className="text-gray-500">Status:</span>{' '}
                        <span className="text-amber-700 font-medium">Flagged</span>
                        {order.airbossFlagReason && (
                          <span className="text-amber-600"> — {order.airbossFlagReason}</span>
                        )}
                      </div>
                    )}
                  </div>
                  {resubmitError && (
                    <div className="mt-3 bg-red-50 border border-red-200 rounded-md p-3 text-sm">
                      <p className="font-medium text-red-800 mb-1">Resubmit failed</p>
                      <p className="text-red-700 font-mono text-xs whitespace-pre-wrap">{resubmitError}</p>
                    </div>
                  )}
                </div>
              )}

              {/* AI Analysis — show for pending orders with failed/flagged status, or any order with an active/completed AI analysis */}
              {((order.airbossJobFailed || order.airbossFlagged) && order.status === 'pending' || order.aiStatus === 'complete' || order.aiStatus === 'started' || order.aiStatus === 'fetching-context' || order.aiStatus === 'analyzing') && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    AI Analysis <span className="text-xs font-normal text-amber-600">(EXPERIMENTAL)</span>
                  </h4>

                  {/* Button: show when no analysis or errored */}
                  {(!order.aiStatus || order.aiStatus === 'error') && (
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <AiModelSelect
                          id={`ai-model-${order.date}-${order.orderId}`}
                          value={aiModel}
                          onChange={setAiModel}
                          disabled={aiAnalysis.isPending}
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            aiAnalysis.mutate({ date: order.date, orderId: order.orderId, aiModel });
                          }}
                          disabled={aiAnalysis.isPending}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-purple-50 text-purple-700 hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                          </svg>
                          {aiAnalysis.isPending ? 'Starting...' : order.aiStatus === 'error' ? 'Retry Analysis' : 'Analyze with AI'}
                        </button>
                      </div>
                      {order.aiStatus === 'error' && order.aiError && (
                        <p className="mt-1 text-sm text-red-600">{order.aiError}</p>
                      )}
                    </div>
                  )}

                  {/* Progress: show when in progress */}
                  {(order.aiStatus === 'started' || order.aiStatus === 'fetching-context' || order.aiStatus === 'analyzing') && (
                    <div className="flex items-center gap-2 text-sm text-purple-700">
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      {order.aiStatus === 'started' && 'Starting analysis...'}
                      {order.aiStatus === 'fetching-context' && 'Fetching order context...'}
                      {order.aiStatus === 'analyzing' && 'Analyzing with Claude...'}
                    </div>
                  )}

                  {/* Results: show when complete */}
                  {order.aiStatus === 'complete' && (
                    <div className="space-y-3">
                      {/* Confidence badge */}
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          order.aiConfidence === 'high'
                            ? 'bg-green-100 text-green-800'
                            : order.aiConfidence === 'medium'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                        }`}>
                          {order.aiConfidence} confidence
                        </span>
                        {order.aiAnalyzedAt && (
                          <span className="text-xs text-gray-500">
                            {new Date(order.aiAnalyzedAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
                          </span>
                        )}
                      </div>

                      {/* Suggestion text */}
                      <div className="bg-purple-50 border border-purple-200 rounded-md p-3 text-sm text-gray-800 prose prose-sm max-w-none prose-headings:text-gray-900 prose-strong:text-gray-900 prose-li:my-0.5">
                        <ReactMarkdown>{order.aiSuggestion || ''}</ReactMarkdown>
                      </div>

                      {/* Missing customer identifier — needs CS to supply the real value, not a cancellation */}
                      {order.airbossJobFailed && !order.aiSuggestedPayload && order.slackCancelReason?.startsWith('Missing Customer Detail:') ? (
                        <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800">
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          Customer detail needed — {order.slackCancelReason.replace('Missing Customer Detail:', '').trim()}
                        </div>
                      ) : /* Cancellation suggested — failed-job analysis that returned no payload means AI recommended cancel */
                      order.airbossJobFailed && !order.aiSuggestedPayload ? (
                        <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-800">
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                          </svg>
                          Cancellation suggested{order.slackCancelReason ? ` — ${order.slackCancelReason}` : ''}
                        </div>
                      ) : order.aiPayloadDiff ? (
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">Changes</p>
                          <pre className="bg-gray-900 text-gray-100 rounded-md p-3 text-xs overflow-x-auto max-h-48 overflow-y-auto">
                            {order.aiPayloadDiff.split('\n').map((line, i) => (
                              <span key={i} className={
                                line.startsWith('+ ') ? 'text-green-400' :
                                line.startsWith('- ') ? 'text-red-400' :
                                line.startsWith('~ ') ? 'text-yellow-300' : ''
                              }>
                                {line}{'\n'}
                              </span>
                            ))}
                          </pre>
                        </div>
                      ) : null}

                      {/* Full suggested payload (collapsible) */}
                      {order.aiSuggestedPayload && (
                        <div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowPayload(!showPayload);
                            }}
                            className="text-xs text-gray-400 hover:text-gray-600"
                          >
                            {showPayload ? 'Hide' : 'Show'} full suggested payload
                          </button>
                          {showPayload && (
                            <pre className="mt-2 bg-gray-900 text-gray-100 rounded-md p-3 text-xs overflow-x-auto max-h-64 overflow-y-auto">
                              {order.aiSuggestedPayload}
                            </pre>
                          )}
                        </div>
                      )}

                      {/* Action buttons row */}
                      <div className="flex items-center gap-3">
                        {/* Export Job button — only for failed-job orders with a suggested payload */}
                        {order.aiSuggestedPayload && order.exportStatus !== 'submitted' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm('Export this order to CHQ with the AI-suggested payload?')) {
                                exportJobMutation.mutate({ date: order.date, orderId: order.orderId });
                              }
                            }}
                            disabled={exportJobMutation.isPending}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                              order.exportStatus === 'error'
                                ? 'bg-red-50 text-red-700 hover:bg-red-100'
                                : 'bg-green-50 text-green-700 hover:bg-green-100'
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                            </svg>
                            {exportJobMutation.isPending ? 'Exporting...' : order.exportStatus === 'error' ? 'Retry Export' : 'Export Job'}
                          </button>
                        )}

                        {/* Export success indicator */}
                        {order.exportStatus === 'submitted' && (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-green-100 text-green-800">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Exported
                            {order.exportedAt && (
                              <span className="text-xs text-green-600 ml-1">
                                {new Date(order.exportedAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
                              </span>
                            )}
                          </span>
                        )}

                        {/* Re-analyze button */}
                        <div className="flex flex-wrap items-center gap-2">
                          <AiModelSelect
                            id={`reanalysis-model-${order.date}-${order.orderId}`}
                            value={aiModel}
                            onChange={setAiModel}
                            disabled={aiAnalysis.isPending}
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              aiAnalysis.mutate({ date: order.date, orderId: order.orderId, aiModel });
                            }}
                            disabled={aiAnalysis.isPending}
                            className="text-sm text-purple-600 hover:text-purple-800 font-medium disabled:opacity-50"
                          >
                            {aiAnalysis.isPending ? 'Starting...' : 'Re-analyze'}
                          </button>
                        </div>
                      </div>

                      {/* Export error — from mutation response (immediate) or DynamoDB (persisted) */}
                      {(exportJobMutation.data?.error || (order.exportStatus === 'error' && order.exportError)) && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm">
                          <p className="font-medium text-red-800 mb-1">Export failed</p>
                          <p className="text-red-700 font-mono text-xs whitespace-pre-wrap">{exportJobMutation.data?.error || order.exportError}</p>
                          {(exportJobMutation.data?.error || order.exportError || '').includes('[Infrastructure]') && (
                            <p className="mt-2 text-red-600 text-xs">
                              Hint: This usually means the Secrets Manager secret (mo-bot/airboss-cognito) is missing or the Cognito M2M credentials are invalid.
                            </p>
                          )}
                          {(exportJobMutation.data?.error || order.exportError || '').includes('Non-JSON response') && (
                            <p className="mt-2 text-red-600 text-xs">
                              Hint: The airboss export endpoint may not be deployed to prod yet. Check that /api/v2/orders/chq/export/ exists on airboss-prod.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Resolution Info */}
              {order.resolvedAt && (
                <div className="text-sm text-gray-600">
                  Resolved on{' '}
                  {new Date(order.resolvedAt).toLocaleString('en-US', {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                  {order.resolvedBy && ` by ${order.resolvedBy}`}
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add notes about this order..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={handleSaveNotes}
                    disabled={updateOrder.isPending || notes === (order.notes || '')}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
